import {localizePath, parseLocalePath} from './locale-path';
import {DEFAULT_LOCALE, isLocale, type Locale} from './locales';

// The visitor's language choice and the decisions built on it. Browser state
// (localStorage, navigator.languages) is passed in by the caller so this module
// also runs in Workers and in the VitePress docs.

/** Written only when the visitor picks a language in the language switcher. */
export const LOCALE_STORAGE_KEY = 'lopaka_locale';

export interface LocaleStorageReader {
    getItem(key: string): string | null;
}

export interface LocaleStorageWriter {
    setItem(key: string, value: string): void;
}

export function readStoredLocale(storage: LocaleStorageReader): Locale | null {
    try {
        const value = storage.getItem(LOCALE_STORAGE_KEY);
        return isLocale(value) ? value : null;
    } catch {
        // Storage can be blocked (privacy settings, sandboxed frames); that is "no choice".
        return null;
    }
}

export function storeLocale(storage: LocaleStorageWriter, locale: Locale): void {
    try {
        storage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
        // The switch still applies to the current page when storage refuses writes.
    }
}

/** First supported language in a `navigator.languages` list, matched by base language (`es-MX` → `es`). */
export function localeFromLanguages(languages: readonly string[]): Locale | null {
    for (const language of languages) {
        const base = language.split('-')[0].toLowerCase();
        if (isLocale(base)) return base;
    }
    return null;
}

/** Stored choice, else the first supported browser language, else English. */
export function resolvePreferredLocale(input: {storedLocale: Locale | null; languages: readonly string[]}): Locale {
    return input.storedLocale ?? localeFromLanguages(input.languages) ?? DEFAULT_LOCALE;
}

/**
 * Language a public localized page should redirect to, or null to stay.
 *
 * 1. A stored choice that differs from the page language wins.
 * 2. Without a stored choice, only the unprefixed English page follows the browser language.
 * 3. Without a stored choice, a page that already has a language prefix stays,
 *    which keeps `/es/...` and `/de/...` reachable for crawlers with English browsers.
 */
export function resolveRedirectLocale(input: {
    pageLocale: Locale;
    storedLocale: Locale | null;
    browserLocale: Locale | null;
}): Locale | null {
    const {pageLocale, storedLocale, browserLocale} = input;
    if (storedLocale) return storedLocale === pageLocale ? null : storedLocale;
    if (pageLocale === DEFAULT_LOCALE && browserLocale && browserLocale !== DEFAULT_LOCALE) return browserLocale;
    return null;
}

/** Address of the same SPA page in the language it should open in, or null to stay. */
export function resolveLocaleRedirectPath(input: {
    path: string;
    storedLocale: Locale | null;
    languages: readonly string[];
}): string | null {
    const page = parseLocalePath(input.path);
    if (!page) return null;
    const target = resolveRedirectLocale({
        pageLocale: page.locale,
        storedLocale: input.storedLocale,
        browserLocale: localeFromLanguages(input.languages),
    });
    return target ? localizePath(page.basePath, target) : null;
}

/** The address language on localized public pages, the preferred language everywhere else. */
export function resolveActiveLocale(path: string, preferredLocale: Locale): Locale {
    return parseLocalePath(path)?.locale ?? preferredLocale;
}
